import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { eStorageFolder } from '../entities/storage.enum';
import { StorageFileUploadResponse } from '../entities/storage.model';

@Injectable({ providedIn: 'root' })
export class StorageService {
  public readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;

  public getObjectNameById(id: string): Observable<string> {
    return this.http.get(
      this.apiUrl.storage + '/Storage/GetObjectName',
      {
        params: { id },
        withCredentials: true,
        responseType: 'text',
      }
    );
  }
  
  public getObjectUrl(id: string): Observable<Record<string, string>> {
    return this.http.get<Record<string, string>>(
      this.apiUrl.storage + '/Storage/GetObjectUrl',
      {
        params: { id },
        withCredentials: true,
      }
    );
  }

  /**
   * `GET {storage_service}/StorageObject/GetObject?id&tenantId`: the object's metadata with its
   * `Url` (the same fields as the upload answer); `null` (204) when there is no such object.
   */
  public getStorageObject(id: string, tenantId: string): Observable<StorageFileUploadResponse | null> {
    return this.http.get<StorageFileUploadResponse | null>(
      `${this.apiUrl.storage_service}/StorageObject/GetObject`,
      { params: { id, tenantId }, withCredentials: true },
    );
  }

  public uploadFile(file: File, folder: eStorageFolder): Observable<HttpEvent<StorageFileUploadResponse>> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    
    const contentType = folder === eStorageFolder.UserPhotos 
      ? 'image/png' 
      : (file.type || this.detectContentTypeFromFileName(file.name));
    formData.append('contentType', contentType);

    return this.http.post<StorageFileUploadResponse>(
      `${this.apiUrl.storage_service}/FileUpload/Upload`,
      formData,
      {
        params: { folder },
        withCredentials: true,
        reportProgress: true,
        observe: 'events',
      }
    );
  }

  public uploadFileAnonymous(file: File, folder: eStorageFolder, tenantId: string): Observable<StorageFileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    
    const contentType = folder === eStorageFolder.UserPhotos 
      ? 'image/png' 
      : (file.type || this.detectContentTypeFromFileName(file.name));
    formData.append('contentType', contentType);

    const headers = new HttpHeaders({
      'X-Tenant-Id': tenantId,
    });

    return this.http.post<StorageFileUploadResponse>(
      `${this.apiUrl.storage_service}/FileUpload/upload-anonymous`,
      formData,
      {
        params: { folder },
        headers,
        withCredentials: false,
      }
    );
  }

  private detectContentTypeFromFileName(filename: string): string {
    const extension = filename?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'pdf':
        return 'application/pdf';
      case 'mp4':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      case 'ogg':
        return 'video/ogg';
      default:
        return 'image/jpeg'; // Default to jpeg for images
    }
  }
}
